import { memo, useMemo, useRef, useState } from "react";
import { MdClose, MdFileCopy, MdInfoOutline } from "react-icons/md";
import { Handle, Position } from "reactflow";

import { useMediaQuery } from "@react-hook/media-query";
import { toNumber } from "lodash";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FinancialMetrics, cn, color } from "@/lib/utils";
import {
  DP22,
  DP24,
  checkTradingLogic,
  getConnectedName,
  getInputLabel,
  getLineColor,
  getShadowColor,
} from "@/helpers/helperFunctions";
import { Checkbox } from "@/components/ui/checkbox";
import { Select } from "@radix-ui/react-select";
import {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import InputField from "./InputField";
import { useFlowContext, useForex } from "../StrategyViewModal";

function capitalize(s: any) {
  return s[0].toUpperCase() + s.slice(1);
}

export default memo(
  ({ id, data, isConnectable }: { data: any; [key: string]: any }) => {
    const wrapperRef: any = useRef();
    const { isForex } = useForex();

    const isMobile = useMediaQuery("only screen and (max-width: 965px)");
    const [otherList, setOtherList] = useState({});
    const filteredInputs = data?.other_inputs?.filter((item: any) => {
      if (isForex) {
        // Keep all except the Price fields
        return !["Take Profit Price", "Stop Loss Price"].includes(item.name);
      } else {
        // Keep all except the Pips fields
        return !["Take Profit Pips", "Stop Loss Pips"].includes(item.name);
      }
    });
    // const [edges] = useEdgesStateSynced();
    const [otherInputs, setOtherInputs] = useState<any>(filteredInputs);
    let globalinputs = {
      "0": "",
      "1": "",
    };
    const { edges, nodes } = useFlowContext();

    return (
      <>
        <Card
          className="max-w-xs w-screen "
          style={getShadowColor(data?.type, data?.title)}
        >
          {data?.inputs_list?.map((port: any, index: number) => {
            const ids =
              data?.inputs?.length > 0
                ? data.inputs[index].id
                : `${port}-${Math.random()}`;
            const input_name =
              data.type === "Trading logic" || data.type === "Data"
                ? getConnectedName(ids, port, nodes, edges)
                : port;

            if (index === 0) globalinputs["0"] = input_name;
            else globalinputs["1"] = input_name;

            return (
              <div className="custom__port" key={index}>
                <Handle
                  type="target"
                  position={Position.Left}
                  className={cn(
                    ` ${data?.title?.split(" ")?.join("")}_${port}`,
                    "port nodrag "
                  )}
                  id={ids}
                  style={{
                    top: 130 + index * 38,
                    ...(isMobile
                      ? { width: 20, height: 20, left: "-10px" }
                      : { width: 13, height: 13 }),
                    backgroundColor: color.palegreen,
                  }}
                  isConnectable={isConnectable}
                />
                <div
                  className="display__port"
                  style={{
                    top: 122 + index * 38,
                    left: "-7px",
                    backgroundColor: "#c4c4c4",
                  }}
                ></div>
                <div
                  className={`absolute left-3 whitespace-nowrap  max-w-xs w-48 overflow-hidden capitalize`}
                  style={{
                    top: 118 + index * 38,
                  }}
                >
                  {input_name === "LTP" ? "Current Price" : input_name}
                </div>
              </div>
            );
          })}
          <CardHeader>
            <CardTitle>
              <div className="flex items-start justify-between">
                <div
                  className="row"
                  style={{
                    justifyContent: "flex-start",
                    maxWidth: "65%",
                  }}
                >
                  <Tooltip delayDuration={100}>
                    <TooltipTrigger asChild>
                      <div className="text-lg truncate">
                        {data.type === "Data" && data.title === "LTP"
                          ? "Current price"
                          : data?.title?.replace("/", " / ")}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {data.type === "Data" && data.title === "LTP"
                        ? "Current price"
                        : data?.title?.replace("/", " / ")}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
              <CardDescription>
                <div
                  className={`mt-2 text-sm pb-2 ${
                    data.title === "Price Data" ? "yellow__bottom" : ""
                  }`}
                  style={{
                    marginBottom:
                      Math.max(
                        data?.inputs_list?.length,
                        data?.outputs_list?.length
                      ) * 40,
                    borderBottom: ` 1px solid ${getLineColor(
                      data?.type,
                      data?.title
                    )}`,
                  }}
                >
                  <div className="flex gap-3 items-center">
                    <Badge>
                      {data.type === "Pattern" ? "Candle Pattern" : data.type}{" "}
                    </Badge>

                    {data?.category && <span>{data?.category}</span>}
                  </div>
                </div>
              </CardDescription>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              {data.type === "Trading logic" ? (
                <div
                  style={{
                    marginLeft: "1rem",
                    marginRight: "1rem",
                    marginTop: "2rem",
                  }}
                >
                  {checkTradingLogic(data?.title) ? (
                    <>
                      {globalinputs["1"] === "LTP"
                        ? "Current Price"
                        : globalinputs["1"]}{" "}
                      <span style={{ fontWeight: "bold" }}>
                        {data.title.toLowerCase()}
                      </span>{" "}
                      {globalinputs["0"] === "LTP"
                        ? "Current Price"
                        : globalinputs["0"]}
                    </>
                  ) : (
                    <>
                      {globalinputs["0"] === "LTP"
                        ? "Current Price"
                        : globalinputs["0"]}{" "}
                      <span style={{ fontWeight: "bold" }}>
                        {data.title.toLowerCase()}
                      </span>{" "}
                      {globalinputs["1"] === "LTP"
                        ? "Current Price"
                        : globalinputs["1"]}
                    </>
                  )}
                </div>
              ) : (
                <div
                  className={`description ${data?.type
                    ?.split(" ")
                    ?.join("")}_category_description`}
                ></div>
              )}

              {data.title === "Alert" && (
                <div style={{ textAlign: "center" }}>Alert method</div>
              )}
              {filteredInputs?.length > 0 ? (
                <Card>
                  <CardContent className="pt-4 grid grid-cols-2 gap-x-2 ">
                    {filteredInputs?.map((item: any, index: number) =>
                      (data.title === "Sell" || data.title === "Buy") &&
                      (item?.name === "Trailing Stop Loss%" ||
                        item?.name === "Trailing Stop Loss" ||
                        item?.name === "Trailling SL %" ||
                        item?.name === "Trailling SL") ? null : (
                        <div
                          key={index}
                          className={"flex flex-col  "}
                          style={
                            item?.name === "Take Profit %" ||
                            item?.name === "Stop Loss %"
                              ? {
                                  zIndex: 2052,
                                  boxSizing: "border-box",
                                  position: "relative",
                                  borderBottomWidth: "2px",
                                  borderTopWidth: "2px",
                                }
                              : {}
                          }
                        >
                          <div className="flex items-center gap-2 ">
                            {item?.type === "checkbox" && (
                              <div className="flex items-center justify-center col-span-2">
                                <Checkbox
                                  checked={
                                    otherInputs?.find(
                                      (i: any) => i?.name === item?.name
                                    )?.checked
                                  }
                                  onCheckedChange={(val: any) => {
                                    setOtherInputs([
                                      ...otherInputs?.filter(
                                        (i: any) => i?.name !== item?.name
                                      ),
                                      { ...item, checked: val },
                                    ]);
                                    item.checked = val as boolean;
                                    return;
                                  }}
                                />
                              </div>
                            )}
                            {data.type === "Indicator" &&
                            item?.name === "real" ? (
                              <span>Type</span>
                            ) : (
                              getInputLabel(item?.name)
                            )}
                          </div>

                          {item?.type === "dropdown" &&
                          (item?.options?.length as number) > 0 ? (
                            <div
                              style={{ position: "relative" }}
                              onClick={() =>
                                setOtherList((prev: any) => ({
                                  ...prev,
                                  [item.name]: !prev[item.name],
                                }))
                              }
                            >
                              <Select
                                disabled
                                value={item?.value as string}
                                onValueChange={(val) => (item.value = val)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectGroup>
                                    {item?.options?.map?.(
                                      (i: any, index: number) => (
                                        <SelectItem value={i} key={index}>
                                          {capitalize(i?.toLowerCase())}
                                        </SelectItem>
                                      )
                                    )}
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                            </div>
                          ) : (
                            <></>
                          )}
                          {item?.type === "input" && (
                            <InputField
                              item={item}
                              other_inputs={filteredInputs}
                              id={data.id}
                            />
                          )}
                        </div>
                      )
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div style={{ marginBottom: "0px" }} />
              )}
            </div>
          </CardContent>
        </Card>

        {data?.outputs_list?.map((port: any, index: number) => {
          const ids =
            data?.outputs?.length > 0
              ? data.outputs[index].id
              : `output-port-${Math.random()}`;

          return (
            <div className="custom__port" key={index}>
              <div
                className="label__left"
                style={{
                  position: "absolute",
                  top: 118 + index * 38,
                  right: "12px",
                  textTransform:
                    data?.type === "Indicator" ? "uppercase" : "none",
                }}
              >
                {port}
              </div>
              <Handle
                type="source"
                position={Position.Right}
                className={cn(
                  ` ${data?.title?.split(" ")?.join("")}_${port}`,
                  "port nodrag"
                )}
                id={ids}
                style={{
                  top: 130 + index * 38,
                  ...(isMobile
                    ? { width: 20, height: 20, right: "-9px" }
                    : { width: 13, height: 13 }),
                  backgroundColor: color.palegreen,
                }}
                isConnectable={isConnectable}
              />

              <div
                className="display__port"
                style={{
                  top: 122 + index * 38,
                  right: "-7px",
                  backgroundColor: "#c4c4c4",
                }}
              ></div>
            </div>
          );
        })}
      </>
    );
  }
);
